import { ref } from 'vue';
import type { Ref } from 'vue';
import { createInsightSavedHint, deleteInsightSavedHint } from '@/api/insights';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { restoreSavedInsightHint } from '@/components/insights/saved/use-saved-insights-pagination';

const successMessage = (saved: boolean) =>
  saved ? '発想のヒントを保存しました' : '保存を解除しました';

const failureMessage = (saved: boolean) =>
  saved
    ? '保存に失敗しました。しばらくお待ちの上、再度お試しください。'
    : '保存の解除に失敗しました。しばらくお待ちの上、再度お試しください。';

/**
 * 保存しても元の発想のヒント（payload）の saved は更新されない。
 * タブ切り替えや画面移動でボタンが作り直されたときに古い値へ戻らないよう、確定した状態をヒントごとに覚えておく。
 */
const confirmedSavedStates = new Map<string, boolean>();

export const clearConfirmedInsightSavedStates = (): void => {
  confirmedSavedStates.clear();
};

export const useInsightSaveToggle = (
  hintUuid: string,
  initialSaved: boolean,
  onSavedStateConfirmed?: (saved: boolean) => void,
): {
  saved: Readonly<Ref<boolean>>;
  syncedSaved: Readonly<Ref<boolean>>;
  settledSaved: Readonly<Ref<boolean>>;
  toggle: () => void;
} => {
  const { createSnackbar } = useSnackbar();

  const initial = confirmedSavedStates.get(hintUuid) ?? initialSaved;
  const saved = ref(initial);
  const syncedSaved = ref(initial);
  const settledSaved = ref(initial);
  const sending = ref(false);

  const sendPendingChange = async (): Promise<void> => {
    const target = saved.value;
    if (target === syncedSaved.value) return;

    try {
      await (target
        ? createInsightSavedHint(hintUuid)
        : deleteInsightSavedHint(hintUuid));
    } catch {
      saved.value = syncedSaved.value;
      createSnackbar({ message: failureMessage(target), type: 'error' });
      return;
    }

    syncedSaved.value = target;
    confirmedSavedStates.set(hintUuid, target);
    if (target) restoreSavedInsightHint(hintUuid);
    onSavedStateConfirmed?.(target);
    createSnackbar({
      message: successMessage(target),
      type: 'success',
      undoFunc: target ? undefined : restoreSave,
    });
    return sendPendingChange();
  };

  const sync = async (): Promise<void> => {
    if (sending.value) return;
    sending.value = true;
    try {
      await sendPendingChange();
    } finally {
      sending.value = false;
    }
    settledSaved.value = syncedSaved.value;
  };

  const restoreSave = () => {
    saved.value = true;
    void sync();
  };

  const toggle = () => {
    saved.value = !saved.value;
    void sync();
  };

  return { saved, syncedSaved, settledSaved, toggle };
};
